import {
  CANADA_STATES,
  CANADA_TERRITORIES,
  JURISDICTION_AREAS,
  UNITED_STATES_STATES,
  UNITED_STATES_TERRITORIES,
} from '@components/CaseSettingsBlocks/Jurisdiction/utils';
import { AccessType } from 'services/ClaimscoreApiService/shared/types';
import { ColumnKey, RequiredColumnGroup } from './types';

const emailRegex = /^(?=.{7,60}$)[^@\s]+@[^@\s]+\.[^@\s]+$/u;
const phoneRegex = /^[\d\-().\s+]{7,20}$/u;
const addressRegexes = [/^(?=[\s\S]{1,200}$)[\s\S]+$/u];

export const EMAIL_FIELDS = [ColumnKey.ContactEmail, ColumnKey.PaymentEmail];

export const PHONE_FIELDS = [ColumnKey.ContactPhone, ColumnKey.PaymentPhone];

export const REQUIRED_COLUMN_GROUPS: RequiredColumnGroup[] = [
  {
    groupKey: 'email',
    keys: EMAIL_FIELDS,
    required: false,
    recommended: true,
    label: 'Email',
  },
  {
    groupKey: 'phone',
    keys: PHONE_FIELDS,
    required: false,
    recommended: true,
    label: 'Phone',
  },
];

export const JURISDICTION_MATCHING_VALUES = [
  ...UNITED_STATES_STATES.map((s) => s.label.toLowerCase()),
  ...UNITED_STATES_STATES.map((s) => s.value.toLowerCase()),
  ...UNITED_STATES_TERRITORIES.map((s) => s.label.toLowerCase()),
  ...UNITED_STATES_TERRITORIES.map((s) => s.value.toLowerCase()),
  ...CANADA_STATES.map((s) => s.label.toLowerCase()),
  ...CANADA_STATES.map((s) => s.value.toLowerCase()),
  ...CANADA_TERRITORIES.map((s) => s.label.toLowerCase()),
  ...CANADA_TERRITORIES.map((s) => s.value.toLowerCase()),
];

export const COUNTRIES_MATCHING_VALUES = [
  ...JURISDICTION_AREAS.map((area) => area.label),
  ...JURISDICTION_AREAS.map((area) => area.value),
  ...[
    'US',
    'us',
    'USA',
    'usa',
    'united states',
    'United States',
    'UNITED STATES',
    'United States of America',
    'united states of america',
    'ca',
    'CA',
    'CANADA',
    'canada',
    'Canada',
  ],
];

export const EXPECTED_COLUMN_FIELDS = [
  // REQUIRED FIELDS
  {
    key: ColumnKey.CaseId,
    labels: [
      'caseID',
      'case_id',
      'Case ID',
      'CaseId',
      'caseId',
      'Case Id',
      'case id',
    ],
    required: true,
    recommended: false,
  },
  {
    key: ColumnKey.AdminId,
    labels: [
      'adminID',
      'admin_id',
      'Admin ID',
      'AdminId',
      'adminId',
      'Admin Id',
      'admin id',
    ],
    required: true,
    recommended: false,
  },
  {
    key: ColumnKey.AccessType,
    labels: ['accessType', 'access_type', 'Access Type'],
    allowedValues: Object.values(AccessType).map((v) => v.toLowerCase()),
    required: true,
    recommended: false,
  },
  // REQUIRED FIELDS
  {
    key: ColumnKey.SubmissionDate,
    labels: [
      'submissionDate',
      'submission_date',
      'Submission Date',
      'Submitted At',
      'Date Submitted',
    ],
    valueRegex: [
      // 2025-06-25
      /^\d{4}-\d{2}-\d{2}$/u,
      // 06/25/2025
      /^\d{2}\/\d{2}\/\d{4}$/u,
      // 2025/06/25
      /^\d{4}\/\d{2}\/\d{2}$/u,
      // 25-Jun-2025
      /^\d{2}-[A-Za-z]{3}-\d{4}$/u,
      // 2025-06-25T12:34:56Z or 2023-06-10T4:20:00.000Z (with single-digit hours)
      /^\d{4}-\d{2}-\d{2}[T\s]\d{1,2}:\d{1,2}:\d{1,2}(?:\.\d+)?(?:Z)?$/u,
    ],
    required: false,
    recommended: true,
  },
  {
    key: ColumnKey.SubmissionType,
    labels: ['submissionType', 'submission_type', 'Submission Type'],
    allowedValues: ['claim', 'other'],
    required: false,
    recommended: false,
  },
  {
    key: ColumnKey.EntityType,
    labels: ['entityType', 'entity_type', 'Entity Type'],
    allowedValues: ['individual', 'organization'],
    required: false,
    recommended: false,
  },
  {
    key: ColumnKey.OrganizationName,
    labels: ['organizationName', 'organization_name', 'Organization Name'],
    required: false,
    recommended: false,
  },
  {
    key: ColumnKey.IsOnlineSubmission,
    labels: [
      'isOnlineSubmission',
      'is_online_submission',
      'Is Online Submission',
      'onlineSubmission',
      'online_submission',
      'submissionChannel',
      'submission_channel',
      'Submission Channel',
    ],
    required: false,
    recommended: true,
  },
  {
    key: ColumnKey.DocumentationProvided,
    labels: [
      'documentationProvided',
      'documentation_provided',
      'Documentation Provided',
      'evidenceProvided',
      'evidence_provided',
      'Evidence Provided',
      'evidence',
    ],
    required: false,
    recommended: true,
  },
  {
    key: ColumnKey.FirstName,
    labels: ['firstName', 'first_name', 'First Name'],
    required: false,
    recommended: true,
  },
  {
    key: ColumnKey.LastName,
    labels: ['lastName', 'last_name', 'Last Name'],
    required: false,
    recommended: true,
  },
  {
    key: ColumnKey.FullName,
    labels: ['fullName', 'full_name', 'Full Name'],
    required: false,
    recommended: false,
  },
  {
    key: ColumnKey.Address,
    labels: ['address', 'Address', 'address1', 'Address 1'],
    valueRegex: addressRegexes,
    required: false,
    recommended: true,
  },
  {
    key: ColumnKey.Address2,
    labels: ['address2', 'Address2', 'Address 2', 'address_line2'],
    valueRegex: addressRegexes,
    required: false,
    recommended: false,
  },
  {
    key: ColumnKey.City,
    labels: ['city', 'City'],
    required: false,
    recommended: true,
  },
  {
    key: ColumnKey.State,
    labels: ['states', 'States', 'state', 'State', 'province', 'Province'],
    allowedValues: JURISDICTION_MATCHING_VALUES,
    required: false,
    recommended: true,
  },
  {
    key: ColumnKey.Country,
    labels: ['country', 'Country'],
    allowedValues: COUNTRIES_MATCHING_VALUES,
    required: false,
    recommended: false,
  },
  {
    key: ColumnKey.Zip,
    labels: ['zip', 'Zip', 'zipcode', 'Zip Code', 'zipCode', 'postal_code'],
    valueRegex: [
      // US (5 digits or 5+4)
      /^\d{5}(?:-\d{4})?$/u,
      // Canada
      /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/u,
      // US zip with leading zeros removed (1-4 digits) - will be padded
      /^\d{1,4}$/u,
    ],
    required: false,
    recommended: true,
  },
  {
    key: ColumnKey.ContactEmail,
    labels: [
      'contactEmail',
      'contact_email',
      'Contact Email',
      'email',
      'Email',
      'emailAddress',
      'email_address',
      'Email Address',
    ],
    valueRegex: emailRegex,
    required: false,
    recommended: true,
  },
  {
    key: ColumnKey.PaymentEmail,
    labels: ['paymentEmail', 'Payment Email', 'payment_email'],
    valueRegex: emailRegex,
    required: false,
    recommended: false,
  },
  {
    key: ColumnKey.ContactPhone,
    labels: [
      'contactPhone',
      'contact_phone',
      'Contact Phone',
      'phone',
      'Phone',
      'phoneNumber',
      'phone_number',
      'Phone Number',
    ],
    valueRegex: phoneRegex,
    required: false,
    recommended: true,
  },
  {
    key: ColumnKey.PaymentPhone,
    labels: ['paymentPhone', 'Payment Phone', 'payment_phone'],
    valueRegex: phoneRegex,
    required: false,
    recommended: false,
  },
  {
    key: ColumnKey.ClassMemberId,
    labels: [
      'classMemberID',
      'classMemberId',
      'Class Member ID',
      'class_member_id',
    ],
    valueRegex: /^[A-Za-z0-9]{8,36}$/u,
    required: false,
    recommended: false,
  },
  {
    key: ColumnKey.AuthenticationPin,
    labels: ['authenticationPin', 'Authentication PIN', 'authentication_pin'],
    valueRegex: /^\d{6,10}$/u,
    required: false,
    recommended: false,
  },
  {
    key: ColumnKey.IpAddress,
    labels: [
      'ipAddress',
      'IP Address',
      'ip_address',
      'ip',
      'IP',
      'Ip',
      'Ip Address',
      'ipaddress',
    ],
    valueRegex: [
      // IPv4
      /^(?:\d{1,3}\.){3}\d{1,3}$/u,
      // IPv6 (including compressed notation)
      /^(?:(?:[0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}|(?:[0-9a-fA-F]{1,4}:){1,7}:|(?:[0-9a-fA-F]{1,4}:){1,6}:[0-9a-fA-F]{1,4}|(?:[0-9a-fA-F]{1,4}:){1,5}(?::[0-9a-fA-F]{1,4}){1,2}|(?:[0-9a-fA-F]{1,4}:){1,4}(?::[0-9a-fA-F]{1,4}){1,3}|(?:[0-9a-fA-F]{1,4}:){1,3}(?::[0-9a-fA-F]{1,4}){1,4}|(?:[0-9a-fA-F]{1,4}:){1,2}(?::[0-9a-fA-F]{1,4}){1,5}|[0-9a-fA-F]{1,4}:(?::[0-9a-fA-F]{1,4}){1,6}|:(?:(?::[0-9a-fA-F]{1,4}){1,7}|:))$/u,
    ],
    required: false,
    recommended: true,
  },
  {
    key: ColumnKey.Referrer,
    labels: ['referrer', 'Referrer'],
    required: false,
    recommended: false,
  },
  {
    key: ColumnKey.PassesCaseSpecificValidation,
    labels: [
      'passesCaseSpecificValidation',
      'Passes Case Specific Validation',
      'passes_case_specific_validation',
    ],
    required: false,
    recommended: false,
  },
];

export const COLUMN_NUMBER_MIN = 3;
export const MATCH_THRESHOLD = 0.7;
export const MIN_LOADING_MS = 5000;
